import type { JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { TrialVisitDto } from '@ctms/contracts';
import { visitLabel } from '@ctms/shared';

import { patientsApi } from '../../patients.api.js';
import { patientQueryKeys } from '../../patients.query-keys.js';
import { SCHEDULE_ACTION_LABELS } from '../../schedule-labels.js';

/** `createdAt` (ISO) → the wireframe's local `YYYY-MM-DD HH:mm` form. */
function formatDecisionTimestamp(iso: string): string {
  const date = new Date(iso);
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
}

/**
 * `trialVisitId` → the schedule table's presentation (`V<n> <name>`, mail 6
 * D.5). Catalog rows are never hard-deleted, so a removed visit's label stays
 * resolvable (REQ-PAT-024); an unresolvable id renders as a dash — visible,
 * never blank (`buildVisitRows` convention). A #41 template-preset entry
 * (`trialVisitId: null`) never reaches this helper — it carries its own name.
 */
function resolveCatalogLabel(trialVisitId: number, catalog: TrialVisitDto[]): string {
  const position = catalog.find((item) => item.id === trialVisitId);
  return position
    ? `V${position.visitNumber} ${visitLabel(position.visitName1, position.visitName2)}`
    : '—';
}

export interface ScheduleHistoryPanelProps {
  /** The chart owning the participation (`patients.id`). */
  patientId: number;
  /** The participation whose change log is shown (`trial_enrollments.id`). */
  enrollmentId: number;
  /** Protocol catalog of the active trial — resolves the log rows' positions. */
  catalog: TrialVisitDto[];
}

/**
 * „Historia zmian harmonogramu (tylko do odczytu)" — the inline change log
 * under the visit table of the „Wizyty pacjenta" section (issue #29 B6/B7,
 * REQ-PAT-033; approved wireframe
 * `spec/domains/patients-pathways/ui/manual-schedule-changes.ui.md`, gate of
 * 2026-10-22: inline table, newest first).
 *
 * The log covers EXCLUSIVELY manual additions/removals of visits — booking a
 * day („Umawiaj") leaves its trail in the Cockpit domain, not here (gate
 * decision 5). Entries are immutable; the `removed` entry outlives the
 * deleted visit. Read-only users browse the log like the schedule itself.
 */
export function ScheduleHistoryPanel({
  patientId,
  enrollmentId,
  catalog,
}: ScheduleHistoryPanelProps): JSX.Element {
  const logQuery = useQuery({
    queryKey: patientQueryKeys.scheduleLog(patientId, enrollmentId),
    queryFn: () => patientsApi.getScheduleLog(patientId, enrollmentId),
  });
  const items = logQuery.data?.items ?? [];

  return (
    <div className="mt-4 border-t border-slate-800 pt-4">
      <h3 id="schedule-history-title" className="text-xs font-semibold text-slate-200">
        Historia zmian harmonogramu (tylko do odczytu)
      </h3>
      {logQuery.isLoading ? (
        <div role="status" className="mt-2 space-y-2">
          <p className="text-xs text-slate-400">Ładowanie historii zmian harmonogramu…</p>
          <div className="h-16 animate-pulse rounded-md bg-slate-800/40" />
        </div>
      ) : logQuery.isError ? (
        <div role="alert" className="mt-2 rounded-md border border-rose-800/70 bg-rose-950/40 p-3">
          <p className="text-xs text-rose-200">
            Nie udało się wczytać historii zmian harmonogramu.
          </p>
          <button
            type="button"
            onClick={() => {
              void logQuery.refetch();
            }}
            className="mt-2 rounded-md border border-rose-700 px-3 py-1.5 text-xs text-rose-200"
          >
            Spróbuj ponownie
          </button>
        </div>
      ) : items.length === 0 ? (
        <p className="mt-2 text-xs text-slate-400">
          <span className="font-medium text-slate-300">Brak ręcznych zmian harmonogramu.</span>{' '}
          Wizyty w tabeli pochodzą z planu udziału; pierwszy wpis pojawi się tutaj po ręcznym
          dodaniu lub usunięciu wizyty.
        </p>
      ) : (
        <table className="mt-2 w-full text-left text-xs">
          <thead>
            <tr className="border-b border-slate-800 text-[11px] tracking-wide text-slate-500">
              <th scope="col" className="px-2 py-1.5 font-medium">
                Data i czas
              </th>
              <th scope="col" className="px-2 py-1.5 font-medium">
                Kto
              </th>
              <th scope="col" className="px-2 py-1.5 font-medium">
                Zmiana
              </th>
              <th scope="col" className="px-2 py-1.5 font-medium">
                Wizyta
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-b border-slate-800/60 text-slate-300">
                <td className="px-2 py-1.5 whitespace-nowrap text-slate-400">
                  {formatDecisionTimestamp(item.createdAt)}
                </td>
                <td className="px-2 py-1.5">{item.userDisplayName ?? '—'}</td>
                <td className="px-2 py-1.5">{SCHEDULE_ACTION_LABELS[item.action]}</td>
                <td className="px-2 py-1.5 text-slate-100">
                  {item.trialVisitId === null
                    ? (item.presetName ?? '—')
                    : resolveCatalogLabel(item.trialVisitId, catalog)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
